import React, { useState, useEffect, useRef } from 'react';
import { useUser } from '../Context/UserContext';
import '../Styles/Profile.css';
import { API_BASE_URL } from '../config';
import { BsPerson, BsBag, BsHeart, BsPersonGear } from 'react-icons/bs';
import ReactCountryFlag from 'react-country-flag';

const Profile = () => {
    const { user, updateUser } = useUser();
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        country: '',
        city: '',
        phone_number: '',
        address1: '',
        address2: '',
        street: '',
        building: '',
        floor: ''
    });
    const [isCountryDropdownOpen, setCountryDropdownOpen] = useState(false);
    const countryDropdownRef = useRef(null);

    const countryOptions = [
        { value: '+1', label: 'United States', iso: 'US' },
        { value: '+44', label: 'United Kingdom', iso: 'GB' },
        { value: '+91', label: 'India', iso: 'IN' },
        { value: '+971', label: 'UAE', iso: 'AE' },
        { value: '+20', label: 'Egypt', iso: 'EG' },
        { value: '+966', label: 'Saudi Arabia', iso: 'SA' },
        { value: '+965', label: 'Kuwait', iso: 'KW' },
        { value: '+974', label: 'Qatar', iso: 'QA' },
        { value: '+973', label: 'Bahrain', iso: 'BH' },
        { value: '+968', label: 'Oman', iso: 'OM' },
        { value: '+962', label: 'Jordan', iso: 'JO' },
        { value: '+961', label: 'Lebanon', iso: 'LB' },
        { value: '+963', label: 'Syria', iso: 'SY' },
        { value: '+964', label: 'Iraq', iso: 'IQ' },
        { value: '+967', label: 'Yemen', iso: 'YE' },
        { value: '+216', label: 'Tunisia', iso: 'TN' }
    ];

    const getPhoneMaxLength = (countryCode) => {
        const phoneMaxLengths = {
            '+1': 10,
            '+44': 10,
            '+91': 10,
            '+971': 9,
            '+966': 9,
            '+20': 10,
            '+965': 8,
            '+974': 8,
            '+973': 8,
            '+968': 8,
            '+962': 9,
            '+961': 8,
            '+963': 9,
            '+964': 10,
            '+967': 8,
            '+216': 8
        };
        return phoneMaxLengths[countryCode] || 15;
    };

    const getCountryByCode = (countryCode) => {
        return countryOptions.find((option) => option.value === countryCode) || countryOptions.find(o => o.value === '+961');
    };

    const sanitizePhoneNumber = (value, countryCode) => {
        const digits = value.replace(/\D/g, '');
        return digits.slice(0, getPhoneMaxLength(countryCode));
    };

    const getPhoneParts = (phone) => {
        if (!phone) return { code: '+961', number: '' };
        if (phone.includes(' ')) {
            const parts = phone.split(' ');
            return { code: parts[0], number: parts.slice(1).join(' ') };
        }
        const matched = countryOptions.find(o => phone.startsWith(o.value));
        if (matched) return { code: matched.value, number: phone.slice(matched.value.length) };
        return { code: '+961', number: phone };
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (countryDropdownRef.current && !countryDropdownRef.current.contains(event.target)) {
                setCountryDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);
    const [loading, setLoading] = useState(false);
    const [toast, setToast] = useState({ show: false, message: '', type: '' });

    useEffect(() => {
        if (user) {
            setFormData({
                name: user.name || '',
                email: user.email || '',
                country: user.country || '',
                city: user.city || '',
                phone_number: user.phone_number || '',
                address1: user.address1 || '',
                address2: user.address2 || '',
                street: user.street || '',
                building: user.building || '',
                floor: user.floor || ''
            });
        }
    }, [user]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const showToast = (message, type) => {
        setToast({ show: true, message, type });
        setTimeout(() => setToast({ show: false, message: '', type: '' }), 3000);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const response = await fetch(`${API_BASE_URL}/user/profile/${user.id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify(formData)
            });

            const data = await response.json();

            if (response.ok) {
                updateUser(formData);
                showToast('Profile updated successfully', 'success');
            } else {
                showToast(data.message || 'Failed to update profile', 'error');
            }
        } catch (err) {
            console.error(err);
            showToast('Network error, please try again', 'error');
        } finally {
            setLoading(false);
        }
    };

    if (!user) {
        return (
            <div className="profile-container">
                <div className="profile-header">
                    <h1>Please log in to view your profile</h1>
                </div>
            </div>
        );
    }

    return (
        <div className="profile-container">
            <div className="profile-grid">
                <div className="profile-sidebar">
                    <div className="profile-avatar-container">
                        <div className="profile-avatar">
                            {user.name ? user.name.charAt(0).toUpperCase() : 'U'}
                        </div>
                    </div>
                    <div className="profile-user-info">
                        <h2>{user.name}</h2>
                        <p>{user.email}</p>
                    </div>
                    <div className="profile-nav">
                        <div className="profile-nav-item active">
                            <BsPerson size={18} /> Personal Info
                        </div>
                        <div className="profile-nav-item" onClick={() => window.location.href = '/orders'}>
                            <BsBag size={18} /> My Orders
                        </div>
                        <div className="profile-nav-item" onClick={() => window.location.href = '/wishlist'}>
                            <BsHeart size={18} /> Wishlist
                        </div>
                    </div>
                </div>

                <div className="profile-content">
                    <div className="profile-section-title">
                        <BsPersonGear size={22} />
                        Profile Information
                    </div>

                    <form className="profile-form" onSubmit={handleSubmit}>
                        <div className="form-group">
                            <label>Full Name</label>
                            <input
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                placeholder="Your Name"
                                required
                            />
                        </div>
                        <div className="form-group">
                            <label>Email Address</label>
                            <input
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                placeholder="Email Address"
                                required
                            />
                        </div>
                        <div className="form-group">
                            <label>Phone Number</label>
                            <div className="premium-phone-input">
                                <div className="country-selector-wrapper" ref={countryDropdownRef}>
                                    <button
                                        type="button"
                                        className="country-select-trigger"
                                        onClick={() => setCountryDropdownOpen(!isCountryDropdownOpen)}
                                    >
                                        <ReactCountryFlag
                                            svg
                                            countryCode={getCountryByCode(getPhoneParts(formData.phone_number).code)?.iso || 'LB'}
                                            style={{ width: '22px', height: '16px' }}
                                        />
                                        <span className="current-code">{getPhoneParts(formData.phone_number).code}</span>
                                        <i className={`bi bi-chevron-down ${isCountryDropdownOpen ? 'open' : ''}`}></i>
                                    </button>

                                    {isCountryDropdownOpen && (
                                        <div className="country-codes-dropdown">
                                            {countryOptions.map((option) => (
                                                <div
                                                    key={option.value}
                                                    className="country-code-item"
                                                    onClick={() => {
                                                        const { number } = getPhoneParts(formData.phone_number);
                                                        setFormData({
                                                            ...formData,
                                                            phone_number: `${option.value} ${number}`,
                                                            country: option.label
                                                        });
                                                        setCountryDropdownOpen(false);
                                                    }}
                                                >
                                                    <ReactCountryFlag svg countryCode={option.iso} style={{ width: '20px', height: '15px' }} />
                                                    <span className="option-label">{option.label}</span>
                                                    <span className="option-code">{option.value}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <input
                                    type="tel"
                                    className="phone-main-input"
                                    value={getPhoneParts(formData.phone_number).number}
                                    onChange={(e) => {
                                        const { code } = getPhoneParts(formData.phone_number);
                                        const number = sanitizePhoneNumber(e.target.value, code);
                                        setFormData({ ...formData, phone_number: `${code} ${number}` });
                                    }}
                                    placeholder={`Max ${getPhoneMaxLength(getPhoneParts(formData.phone_number).code)} digits`}
                                />
                            </div>
                            <div className="socialmedia-input-hint">Digits only; max {getPhoneMaxLength(getPhoneParts(formData.phone_number).code)} for this country.</div>
                        </div>
                        <div className="form-group">
                            <label>Country</label>
                            <input
                                type="text"
                                name="country"
                                value={formData.country}
                                onChange={handleChange}
                                placeholder="Country"
                            />
                        </div>
                        <div className="form-group">
                            <label>City</label>
                            <input
                                type="text"
                                name="city"
                                value={formData.city}
                                onChange={handleChange}
                                placeholder="Your City"
                            />
                        </div>
                        <div className="form-group">
                            <label>Primary Address</label>
                            <input
                                type="text"
                                name="address1"
                                value={formData.address1}
                                onChange={handleChange}
                            />
                        </div>
                        <div className="form-group">
                            <label>Street</label>
                            <input
                                type="text"
                                name="street"
                                value={formData.street}
                                onChange={handleChange}
                                placeholder="Street Name"
                            />
                        </div>
                        <div className="form-group">
                            <label>Building</label>
                            <input
                                type="text"
                                name="building"
                                value={formData.building}
                                onChange={handleChange}
                                placeholder="Building Name/No"
                            />
                        </div>
                        <div className="form-group">
                            <label>Floor</label>
                            <input
                                type="text"
                                name="floor"
                                value={formData.floor}
                                onChange={handleChange}
                                placeholder="Floor No"
                            />
                        </div>

                        <div className="profile-actions" style={{ alignSelf: 'end' }}>
                            <button type="submit" className="btn-save" disabled={loading} style={{ width: '100%' }}>
                                {loading ? 'Saving...' : 'Save Changes'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            {toast.show && (
                <div className={`toast ${toast.type} show`}>
                    {toast.message}
                </div>
            )}
        </div>
    );
};

export default Profile;
